import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { uploadImage } from "@/lib/upload";
import { useAuth, ROLE_LABEL } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UploadCloud, Loader2, UserCog, KeyRound, Trash2 } from "lucide-react";
import { initials, PageHeader } from "@/components/common";

export default function AdminProfile() {
  const qc = useQueryClient();
  const { checkAuth } = useAuth();
  const { data: profile } = useQuery({ queryKey: ["profile"], queryFn: async () => (await api.get("/auth/profile")).data });
  const [form, setForm] = useState(null);
  const [pw, setPw] = useState({ old_password: "", new_password: "", new_password_confirm: "" });
  const [showPw] = useState(false);
  void showPw;
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (profile && !form) setForm({ nama: profile.nama || "", bio: profile.bio || "", nomor_wa: profile.nomor_wa || "", foto: profile.foto || "" }); }, [profile, form]);

  const saveMut = useMutation({
    mutationFn: async (p) => api.patch("/auth/profile", p),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["profile"] }); checkAuth(); toast.success("Profil diperbarui"); },
    onError: (e) => toast.error(errMsg(e, "Gagal menyimpan")),
  });
  const passMut = useMutation({
    mutationFn: async () => api.post("/auth/change-password", pw),
    onSuccess: () => { toast.success("Kata sandi diperbarui"); setPw({ old_password: "", new_password: "", new_password_confirm: "" }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const onFoto = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setUploading(true);
    try { const url = await uploadImage(f); setForm((p) => ({ ...p, foto: url })); saveMut.mutate({ foto: url }); }
    catch { toast.error("Gagal unggah foto"); } finally { setUploading(false); }
  };

  if (!form) return <div className="glass rounded-2xl p-10 text-center text-slate-400">Memuat profil...</div>;
  const mismatch = pw.new_password_confirm && pw.new_password !== pw.new_password_confirm;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-profile">
      <PageHeader icon={UserCog} title="Profil Saya" subtitle={<>@{profile?.username} · <Badge className="border-0 bg-emerald-100 text-emerald-800">{ROLE_LABEL[profile?.role]}</Badge>{profile?.assigned_ribath && <> · Ribath {profile.assigned_ribath}</>}</>} />

      <div className="glass rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20 ring-2 ring-emerald-100"><AvatarImage src={form.foto} /><AvatarFallback className="bg-emerald-100 text-emerald-700 dark:text-emerald-400 text-xl">{initials(form.nama || "P")}</AvatarFallback></Avatar>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="profile-foto-input" onChange={onFoto} />
            <Button type="button" variant="outline" size="sm" className="gap-2 rounded-xl" data-testid="profile-upload-foto" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Unggah Foto
            </Button>
            {form.foto && <Button type="button" variant="ghost" size="sm" className="gap-2 rounded-xl text-rose-600 hover:text-rose-700 ml-2" data-testid="profile-hapus-foto" onClick={() => { setForm((p) => ({ ...p, foto: "" })); saveMut.mutate({ foto: "" }); }}><Trash2 className="h-4 w-4" /> Hapus Foto</Button>}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs">Nama</Label><Input value={form.nama} data-testid="profile-nama" onChange={(e) => setForm({ ...form, nama: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Nomor WA (untuk OTP)</Label><Input value={form.nomor_wa} data-testid="profile-wa" placeholder="mis. 0812xxxx" onChange={(e) => setForm({ ...form, nomor_wa: e.target.value })} /></div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Biodata / Bio</Label><Textarea value={form.bio} data-testid="profile-bio" placeholder="Ceritakan sedikit tentang Anda..." onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
        <Button className="gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="profile-save" disabled={saveMut.isPending} onClick={() => saveMut.mutate({ nama: form.nama, bio: form.bio, nomor_wa: form.nomor_wa })}><UserCog className="h-4 w-4" /> Simpan Profil</Button>
      </div>

      {profile?.role === "super_admin" && (
      <div className="glass rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400"><KeyRound className="h-5 w-5" /><span className="font-semibold">Ubah Kata Sandi</span></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <PasswordInput placeholder="Sandi lama" value={pw.old_password} data-testid="profile-old-password" onChange={(e) => setPw({ ...pw, old_password: e.target.value })} />
          <PasswordInput placeholder="Sandi baru (min. 8)" value={pw.new_password} data-testid="profile-new-password" onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          <PasswordInput placeholder="Ulangi sandi baru" value={pw.new_password_confirm} data-testid="profile-new-password2" className={mismatch ? "border-rose-400" : ""} onChange={(e) => setPw({ ...pw, new_password_confirm: e.target.value })} />
        </div>
        {mismatch && <p className="text-xs text-rose-600">Konfirmasi sandi tidak sama.</p>}
        <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="profile-save-password" disabled={pw.new_password.length < 8 || pw.new_password !== pw.new_password_confirm || !pw.old_password || passMut.isPending} onClick={() => passMut.mutate()}>Ubah Sandi</Button>
      </div>
      )}
    </div>
  );
}
